import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DocumentTextIcon,
  FolderIcon,
  Cog6ToothIcon,
  ClockIcon,
  StarIcon,
  TagIcon,
  RectangleStackIcon,
  XMarkIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { documentsAPI } from '@api/documents'
import { useAuthStore } from '@stores/authStore'

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onClose }) => {
  const location = useLocation()
  const { isAuthenticated } = useAuthStore()
  
  // Fetch document statistics
  const { data: stats } = useQuery({
    queryKey: ['document-statistics'],
    queryFn: () => documentsAPI.getStatistics(),
    enabled: isAuthenticated,
    refetchInterval: 60000, // Refetch every minute
  })

  const navigation = [
    {
      name: 'Recent Documents',
      href: '/documents?filter=recent',
      icon: ClockIcon,
      current: location.pathname === '/documents' && location.search.includes('recent'),
    },
    {
      name: 'Starred',
      href: '/documents?filter=starred',
      icon: StarIcon,
      current: location.pathname === '/documents' && location.search.includes('starred'),
    },
    {
      name: 'All Documents',
      href: '/documents',
      icon: FolderIcon,
      current: location.pathname === '/documents' && !location.search,
    },
    {
      name: 'Workspaces',
      href: '/workspaces',
      icon: RectangleStackIcon,
      current: location.pathname.startsWith('/workspaces'),
    },
    {
      name: 'Templates',
      href: '/templates',
      icon: DocumentTextIcon,
      current: location.pathname === '/templates',
    },
    {
      name: 'Tags',
      href: '/tags',
      icon: TagIcon,
      current: location.pathname === '/tags',
    },
  ]

  const quickActions = [
    {
      name: 'Settings',
      href: '/settings',
      icon: Cog6ToothIcon,
    },
  ]

  // Close sidebar when clicking a link on mobile
  const handleLinkClick = () => {
    if (window.innerWidth < 1024 && onClose) {
      onClose()
    }
  }

  return (
    <>
      {/* Mobile Overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={onClose}
            />
            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className={clsx(
                'fixed left-0 top-0 bottom-0 z-50 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col w-64 lg:relative lg:z-auto',
                !isOpen && 'hidden lg:flex'
              )}
            >
              {/* Mobile Header */}
              <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 dark:border-gray-700 lg:hidden">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Menu</h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close sidebar"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
            Documents
          </h3>
          
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={handleLinkClick}
              className={clsx(
                'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                item.current
                  ? 'bg-gradient-to-r from-teal-500/10 to-cyan-500/10 dark:from-teal-500/20 dark:to-cyan-500/20 text-teal-700 dark:text-teal-300 border-l-2 border-teal-500 dark:border-teal-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              )}
            >
              <item.icon
                className={clsx(
                  'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                  item.current ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                )}
              />
              <span className="flex-1">{item.name}</span>
            </Link>
          ))}
        </div>

        {/* Quick Stats */}
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3">
            Quick Stats
          </h3>
          <div className="grid grid-cols-2 gap-2 px-3 lg:grid-cols-1 lg:gap-3">
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-teal-100 dark:border-teal-800/50">
              <div className="text-2xl font-bold text-teal-700 dark:text-teal-300">
                {stats?.total_documents?.toLocaleString() || '0'}
              </div>
              <div className="text-xs font-medium text-teal-600 dark:text-teal-400 mt-1">Documents</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800/50">
              <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                {stats?.words_today?.toLocaleString() || '0'}
              </div>
              <div className="text-xs font-medium text-purple-600 dark:text-purple-400 mt-1">Words Today</div>
            </div>
          </div>
        </div>
      </nav>

      {/* Bottom actions */}
      <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
        {quickActions.map((item) => (
          <Link
            key={item.name}
            to={item.href}
            onClick={handleLinkClick}
            className="group flex items-center px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-lg hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200"
          >
            <item.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors" />
            {item.name}
          </Link>
        ))}
      </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar - Always visible on large screens */}
      <div className="hidden lg:flex w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex-col">
        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3">
              Documents
            </h3>
            
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={clsx(
                  'group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                  item.current
                    ? 'bg-gradient-to-r from-teal-500/10 to-cyan-500/10 dark:from-teal-500/20 dark:to-cyan-500/20 text-teal-700 dark:text-teal-300 border-l-2 border-teal-500 dark:border-teal-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                )}
              >
                <item.icon
                  className={clsx(
                    'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                    item.current ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                  )}
                />
                <span className="flex-1">{item.name}</span>
              </Link>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="mb-6">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-3">
              Quick Stats
            </h3>
            <div className="space-y-3 px-3">
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-xl p-4 border border-teal-100 dark:border-teal-800/50">
                <div className="text-2xl font-bold text-teal-700 dark:text-teal-300">
                  {stats?.total_documents?.toLocaleString() || '0'}
                </div>
                <div className="text-xs font-medium text-teal-600 dark:text-teal-400 mt-1">Documents</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-purple-100 dark:border-purple-800/50">
                <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                  {stats?.words_today?.toLocaleString() || '0'}
                </div>
                <div className="text-xs font-medium text-purple-600 dark:text-purple-400 mt-1">Words Today</div>
              </div>
            </div>
          </div>
        </nav>

        {/* Bottom actions */}
        <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
          {quickActions.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="group flex items-center px-3 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 rounded-lg hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200"
            >
              <item.icon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors" />
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </>
  )
}

export default Sidebar